import { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  updateDoc,
  increment
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import logger from '../utils/logger';

/**
 * Hook to fetch all videos for a specific event
 * @param {string} eventId - The event ID
 * @param {boolean} realtime - Whether to use real-time listener (default: false for cost savings)
 * Returns real-time updates of videos OR one-time fetch
 */
export const useVideos = (eventId, realtime = false) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!eventId) {
      setVideos([]);
      setLoading(false);
      return undefined; // Explicitly return undefined (no cleanup needed)
    }

    setLoading(true);
    setError(null);

    // Create query for videos belonging to this event
    const videosQuery = query(
      collection(db, 'videos'),
      where('eventId', '==', eventId),
      orderBy('uploadedAt', 'desc')
    );

    if (realtime) {
      // Real-time listener (for admin dashboard ONLY)
      let unsubscribed = false;

      const unsubscribe = onSnapshot(
        videosQuery,
        (snapshot) => {
          if (unsubscribed) return;
          const videosData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setVideos(videosData);
          setLoading(false);
        },
        (err) => {
          if (unsubscribed) return;
          logger.error('Error fetching videos:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      // Cleanup listener on unmount
      return () => {
        unsubscribed = true;
        unsubscribe();
      };
    } else {
      // One-time fetch (for gallery - saves 99% of read costs!)
      getDocs(videosQuery)
        .then((snapshot) => {
          const videosData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setVideos(videosData);
          setLoading(false);
        })
        .catch((err) => {
          logger.error('Error fetching videos:', err);
          setError(err.message);
          setLoading(false);
        });

      return undefined; // No cleanup needed for one-time fetch
    }
  }, [eventId, realtime]);

  return { videos, loading, error };
};

const mimeToExtension = (mimeType = '') => {
  const map = {
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov',
    'video/x-msvideo': 'avi',
    'video/x-matroska': 'mkv',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/heic': 'heic',
    'image/heif': 'heif'
  };

  return map[mimeType] || 'bin';
};

const getFileExtension = (mediaFile) => {
  if (mediaFile?.name && mediaFile.name.includes('.')) {
    const extension = mediaFile.name.split('.').pop()?.toLowerCase();
    if (extension) return extension;
  }

  return mimeToExtension(mediaFile?.type);
};

/**
 * Upload media to Firebase Storage and create Firestore record
 */
export const uploadVideo = async (mediaFile, eventId, guestName, guestEmail, onProgress) => {
  try {
    onProgress?.(2);

    // Fetch event to check if it's public (for storage metadata)
    const eventRef = doc(db, 'events', eventId);
    const eventSnap = await getDoc(eventRef);
    const event = eventSnap.data();

    // Generate unique filename
    const timestamp = Date.now();
    const extension = getFileExtension(mediaFile);
    const filename = `${eventId}_${timestamp}.${extension}`;
    const storageRef = ref(storage, `videos/${eventId}/${filename}`);

    // Set custom metadata for storage rules
    const metadata = {
      contentType: mediaFile.type || undefined,
      customMetadata: {
        isPublic: event?.isPublic ? 'true' : 'false',
        eventId: eventId,
        mediaType: mediaFile?.type?.startsWith('image/') ? 'image' : 'video'
      }
    };

    // Upload to Firebase Storage with progress updates
    const uploadTask = uploadBytesResumable(storageRef, mediaFile, metadata);
    await new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (!snapshot.totalBytes) return;
          const uploadPct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 90);
          onProgress?.(Math.max(5, uploadPct));
        },
        reject,
        resolve
      );
    });
    onProgress?.(92);

    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);

    // Create Firestore record
    const videoDoc = await addDoc(collection(db, 'videos'), {
      eventId,
      guestName: guestName || 'Anonymous',
      guestEmail: guestEmail || null,
      videoUrl: downloadURL,
      storagePath: storageRef.fullPath,
      duration: mediaFile?.type?.startsWith('video/') ? 0 : null,
      fileSize: mediaFile.size,
      mimeType: mediaFile.type,
      uploadedAt: serverTimestamp(),
      status: 'active',
      mediaType: mediaFile?.type?.startsWith('image/') ? 'image' : 'video'
    });
    onProgress?.(97);

    // Increment video count on event (reuse eventRef from above)
    await updateDoc(eventRef, {
      videoCount: increment(1),
      updatedAt: serverTimestamp()
    });
    onProgress?.(100);

    return { success: true, id: videoDoc.id, url: downloadURL };
  } catch (err) {
    logger.error('Error uploading video:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Delete a video from both Firestore and Storage
 */
export const deleteVideo = async (videoId, storagePath, eventId) => {
  try {
    // Delete from Storage
    if (storagePath) {
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);
    }

    // Delete from Firestore
    const videoRef = doc(db, 'videos', videoId);
    await deleteDoc(videoRef);

    // Decrement video count on event
    const eventRef = doc(db, 'events', eventId);
    await updateDoc(eventRef, {
      videoCount: increment(-1),
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (err) {
    logger.error('Error deleting video:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Update video metadata (e.g., duration after processing)
 */
export const updateVideoMetadata = async (videoId, metadata) => {
  try {
    const videoRef = doc(db, 'videos', videoId);
    await updateDoc(videoRef, metadata);
    return { success: true };
  } catch (err) {
    logger.error('Error updating video metadata:', err);
    return { success: false, error: err.message };
  }
};
